import { createSelector } from '@reduxjs/toolkit';
import { type FC, useCallback, useEffect, useMemo, useState } from 'react';
import { featuresSelector } from '@/support/Selectors';
import { useSelector } from 'react-redux';
import SamplesTable from '@/components/Samples/SamplesTable';
import { Panel, Tab, Tabs } from '@/components/UI/Tabs';
import { SamplePlanTableView } from '@/components/Samples/SamplePlanTableView';
import { useNavigate, useLocation } from 'react-router-dom';
import SamplingSheetList from '@/components/Samples/SamplingSheetList/SamplingSheetList';
import { useQueryParams } from '@/support/Hooks/useQueryParams/useQueryParams';
import { type Nullable } from '@/model/Common.model';

const featureFlagsSelector = createSelector([featuresSelector], (features) => ({
  plannedSamplesEnabled: features?.planned_samples ?? false,
  samplingSheetsEnabled: features?.sampling_sheets ?? false,
}));

type TabConfig = {
  id: string;
  label: string;
  testId: string;
  component: FC;
};

export const SamplesView: FC = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const queryParams = useQueryParams();
  const { plannedSamplesEnabled, samplingSheetsEnabled } = useSelector(featureFlagsSelector);

  // Build tabs array based on feature flags
  const tabs = useMemo<Array<TabConfig>>(() => {
    const availableTabs: Array<TabConfig> = [
      { id: 'list', label: 'Samples', testId: 'samples-tab', component: SamplesTable },
    ];

    if (plannedSamplesEnabled) {
      availableTabs.push({
        id: 'plan',
        label: 'Sampling Plan',
        testId: 'sampling-plan-tab',
        component: SamplePlanTableView,
      });
    }

    if (samplingSheetsEnabled) {
      availableTabs.push({
        id: 'sheet',
        label: 'Sampling Sheet',
        testId: 'sampling-sheet-tab',
        component: SamplingSheetList,
      });
    }

    return availableTabs;
  }, [plannedSamplesEnabled, samplingSheetsEnabled]);

  const tabIdToIndex = useMemo(() => new Map(tabs.map((tab, index) => [tab.id, index])), [tabs]);

  const getTabIndex = useCallback(
    (tabId: Nullable<string>): number => tabIdToIndex.get(tabId ?? 'list') ?? 0,
    [tabIdToIndex]
  );

  const [activeTab, updateActiveTab] = useState<number>(() => getTabIndex(queryParams?.get('tab')));

  const handleTabChange = useCallback(
    (updatedTabIndex: number) => {
      const updatedQueryParams = new URLSearchParams(location.search);
      const tabId = tabs[updatedTabIndex]?.id;
      if (tabId === 'list') {
        updatedQueryParams.delete('tab');
      } else if (tabId !== '') {
        updatedQueryParams.set('tab', tabId);
      }
      navigate({ search: updatedQueryParams.toString() });
    },
    [location.search, navigate, tabs]
  );

  useEffect(() => {
    updateActiveTab(getTabIndex(queryParams?.get('tab')));
  }, [queryParams, getTabIndex]);

  // If only the samples tab is available, render without tabs
  if (tabs.length === 1) {
    return (
      <div className="ph4 pt3">
        <SamplesTable />
      </div>
    );
  }

  return (
    <div className="ph4 pt3">
      <Tabs outerState={[activeTab, handleTabChange]}>
        <div className="mb4 bb b--moon-gray">
          {tabs.map((tab) => (
            <Tab key={tab.id} className="f4" testId={tab.testId}>
              {tab.label}
            </Tab>
          ))}
        </div>

        {tabs.map((tab) => (
          <Panel key={tab.id}>
            <tab.component />
          </Panel>
        ))}
      </Tabs>
    </div>
  );
};
